import type { KdsBoardConfig } from '@shared/kds/table-board';

const BASE_URL = '/api/kitchen';
const AUTH_URL = '/api/auth';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('kitchen_jwt');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((options.headers as Record<string, string>) || {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  const json: ApiResponse<T> = await res.json();

  if (!json.success) {
    throw new Error(json.error || 'Request failed');
  }

  return json.data as T;
}

export async function publicRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  const json: ApiResponse<T> = await res.json();

  if (!json.success) {
    throw new Error(json.error || 'Request failed');
  }

  return json.data as T;
}

// --- Auth (uses /api/auth, not /api/kitchen) ---
export const authApi = {
  login: async (email: string, password: string) => {
    const res = await fetch(`${AUTH_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error || 'Login fallito');
    }
    return json.data as {
      token: string;
      user: { id: string; email: string; name: string; role: string };
    };
  },

  getMyClients: async (token: string) => {
    const res = await fetch(`${AUTH_URL}/me/clients`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error || 'Errore nel recupero dei ristoranti');
    }
    return json.data as { id: string; name: string }[];
  },
};

// --- Stations ---
export const stationsApi = {
  list: (clientId: string) => request<any[]>(`/stations?clientId=${clientId}`),
  get: (id: string) => request<any>(`/stations/${id}`),
};

// --- Orders ---
export const ordersApi = {
  list: (clientId: string, params?: { status?: string; date?: string }) => {
    // Filter out undefined values before building the query string.
    // URLSearchParams converts undefined to the string "undefined", which
    // causes the backend to filter for a literal status of "undefined".
    const defined: Record<string, string> = { clientId };
    if (params?.status !== undefined) defined.status = params.status;
    if (params?.date !== undefined) defined.date = params.date;
    const qs = new URLSearchParams(defined);
    return request<any[]>(`/orders?${qs}`);
  },
  get: (id: string) => request<any>(`/orders/${id}`),
  create: (data: any) => request<any>('/orders', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) =>
    request<any>(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  fireCourse: (id: string) => request<any>(`/orders/${id}/fire-course`, { method: 'POST' }),
  complete: (id: string) => request<any>(`/orders/${id}/complete`, { method: 'POST' }),
};

// --- Items ---
export const itemsApi = {
  updateStatus: (id: string, status: string) =>
    request<any>(`/items/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
  bump: (id: string) => request<any>(`/items/${id}/bump`, { method: 'POST' }),
};

// --- Station view ---
export const stationViewApi = {
  get: (stationId: string, clientId: string) =>
    request<any[]>(`/station-view/${stationId}?clientId=${clientId}`),
};

// --- Public (no auth) ---
export const publicApi = {
  getMenu: (token: string) => request<any>(`/public/menu/${token}`),
  placeOrder: (token: string, data: { items: any[]; customerName?: string; notes?: string }) =>
    request<any>(`/public/order/${token}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// --- Public Payment (no auth) ---
export const publicPaymentApi = {
  createCheckout: (token: string, orderId: string) =>
    request<{ sessionId: string; url: string }>(`/public/checkout/${token}`, {
      method: 'POST',
      body: JSON.stringify({ orderId }),
    }),
  checkPaymentStatus: (sessionId: string) =>
    request<{
      status: string;
      orderId: string;
      orderNumber?: number;
      estimatedMinutes?: number;
    }>(`/public/payment-status/${sessionId}`),
};

// --- Feature 084: Client Config (includes kdsBoardConfig) ---

export interface ClientConfig {
  secondaryCashEnabled: boolean;
  secondaryCashVisible: boolean;
  maxPlafondDiscountPercent: number;
  sociDiscountEnabled: boolean;
  tipsModuleEnabled: boolean;
  kdsBoardConfig: KdsBoardConfig;
  // Feature 113 — blocco ordini cibo
  foodOrderBlockEnabled?: boolean;
  foodOrderBlockActive?: boolean;
  // Feature 148 — cutoff giornata lavorativa (ora Rome, es. 4 = le 04:00).
  // Non ancora esposto dal backend: se assente, lo Storico usa il default 4.
  businessDayCutoffHour?: number;
}

export const clientConfigApi = {
  get: (clientId: string): Promise<ClientConfig> =>
    request<ClientConfig>(`/client-config?clientId=${encodeURIComponent(clientId)}`),
};

// --- Feature 113: Blocco ordini cibo (toggle chef da KDS) ---

export const foodBlockApi = {
  toggle: (
    clientId: string,
    active: boolean,
  ): Promise<{ clientId: string; enabled: boolean; active: boolean; changedAt: string }> =>
    request<{ clientId: string; enabled: boolean; active: boolean; changedAt: string }>(
      '/food-block/toggle',
      {
        method: 'POST',
        body: JSON.stringify({ clientId, active }),
      },
    ),
};

// --- Feature 022: KDS Voice Personalities ---
export interface VoiceAnnounceResponse {
  success?: boolean;
  silent?: boolean;
  audioUrl?: string;
  fallbackText?: string;
  cached?: boolean;
  generatedText?: string;
  source?: string;
}

export const kdsVoiceApi = {
  /**
   * Notifica al backend un evento KDS e riceve audio URL o fallbackText.
   * Mai throw — error path → silent (FR-017).
   */
  announce: (
    eventType: 'new_order' | 'course_fired' | 'item_ready' | 'nag_overdue',
    payload: Record<string, unknown>,
  ) =>
    request<VoiceAnnounceResponse>(`/tts/announce`, {
      method: 'POST',
      body: JSON.stringify({ eventType, payload }),
    }).catch(() => ({ success: false, silent: true }) as VoiceAnnounceResponse),
};
